import React, { useMemo } from 'react'
import { useSourceView } from './UseSourceView.ts'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { useTransformer } from '../Transformer/UseTransformer.tsx'

type SharedUniqueParams = {
  title?: string
  maxBuckets?: number
  minMagnitude?: number
}

const EMPTY_PATH: string[] = []

type Row = {
  bucketKey: string
  bucketLabel: string
  unique: number
  shared: number
  total: number
}

export default function SharedUniqueSink({
  sourceId,
  config
}: {
  sourceId: SourceId
  sinkId: SinkId
  config: SinkConfig<SharedUniqueParams>
}) {
  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0

  const bucketsRes = useTransformer(sourceId, undefined, 'kronaFilteredBuckets', {
    selectedPath,
    gradeThreshold
  })

  const maxBuckets = config.params?.maxBuckets ?? 30
  const minMagnitude = config.params?.minMagnitude ?? 0

  const rows = useMemo(() => {
    if (bucketsRes.status !== 'ready') return [] as Row[]

    const depth = selectedPath.length
    const buckets = bucketsRes.value.slice(0, maxBuckets)

    const sets = buckets.map((b) => {
      const s = new Set<string>()
      for (const v of b.values) {
        const tax = v.taxa[depth] ?? '(n/a)'
        const mag = v.magnitude ?? 0
        if (mag <= minMagnitude) continue
        s.add(tax)
      }
      return s
    })

    const freq = new Map<string, number>()
    for (const s of sets) {
      for (const t of s) freq.set(t, (freq.get(t) ?? 0) + 1)
    }

    const out: Row[] = buckets.map((b, i) => {
      let unique = 0
      let shared = 0
      for (const t of sets[i]!) {
        const f = freq.get(t) ?? 0
        if (f <= 1) unique++
        else shared++
      }
      const total = unique + shared
      return { bucketKey: b.bucketKey, bucketLabel: b.bucketLabel, unique, shared, total }
    })

    out.sort((a, b) => b.total - a.total)
    return out
  }, [bucketsRes, selectedPath.length, maxBuckets, minMagnitude])

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (bucketsRes.status === 'idle' || bucketsRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (rows.length === 0) return <div className="text-sm opacity-70">No data for current selection.</div>

  const title = config.params?.title ?? 'Shared vs unique taxa per bucket'
  const maxTotal = Math.max(...rows.map((r) => r.total))

  return (
    <div className="flex flex-col gap-3 h-full p-4">
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="text-xs opacity-60">
        Level: <span className="font-mono">{selectedPath.join(' › ') || 'Root'}</span>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="grid gap-y-2" style={{ gridTemplateColumns: '260px 1fr 72px 72px 72px', alignItems: 'center' }}>
          {/* Header */}
          <div className="text-xs font-medium opacity-70">Bucket</div>
          <div className="text-xs font-medium opacity-70">Composition</div>
          <div className="text-xs font-medium opacity-70 text-right">Unique</div>
          <div className="text-xs font-medium opacity-70 text-right">Shared</div>
          <div className="text-xs font-medium opacity-70 text-right">Total</div>

          {rows.map((r) => {
            const totalPct = (r.total / (maxTotal || 1)) * 100
            const uniquePct = r.total ? (r.unique / r.total) * 100 : 0
            const sharedPct = 100 - uniquePct

            return (
              <React.Fragment key={r.bucketKey}>
                {/* Bucket label */}
                <div className="text-xs truncate pr-2" title={r.bucketLabel}>
                  {r.bucketLabel}
                </div>

                {/* Bar */}
                <div className="h-4 bg-black/10 rounded overflow-hidden" title={`Unique: ${r.unique}, Shared: ${r.shared}`}>
                  <div className="h-4 flex" style={{ width: `${totalPct}%` }}>
                    <div className="h-4 bg-black/70" style={{ width: `${uniquePct}%` }} />
                    <div className="h-4 bg-black/35" style={{ width: `${sharedPct}%` }} />
                  </div>
                </div>

                {/* Numbers */}
                <div className="text-xs font-mono text-right opacity-70">{r.unique}</div>
                <div className="text-xs font-mono text-right opacity-70">{r.shared}</div>
                <div className="text-xs font-mono text-right opacity-70">{r.total}</div>
              </React.Fragment>
            )
          })}
        </div>
      </div>

      <div className="text-xs opacity-60 flex items-center gap-3">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 bg-black/70 rounded-sm" />
          unique
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 bg-black/35 rounded-sm" />
          shared
        </span>
      </div>
    </div>
  )
}
